'use client';

import DefaultDarkIcon from '@/assets/images/Andritz2.png';
import DefaultIcon from '@/assets/images/Andritz.png';
import { useUserInfo } from '@/domain/account/hooks';
import { type ProductBrief, type ProductSimple } from '@/domain/products/types';
import { type SxProps } from '@andritzot/metris-web-ui';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { ArrowForwardOutlined } from '@mui/icons-material';
import Image from 'next/image';

import { useSolutionStore } from '../../../store/solution';
import AddToCartButton from './AddToCartButton';
import ProductDescription from './ProductDescription';
import ProductName from './ProductName';

type CardProps = {
  product: ProductSimple | ProductBrief;
  layout: 'small' | 'large';
  isInCart?: boolean;
  redirectToProductPage?: boolean;
};

const normalizeImagePath = (src: string): string | null =>
  src ? src.replace('Image', 'image').replace('Icons', 'icons') : null;

const cardImageSrc = (product: ProductSimple | ProductBrief, isSelected: boolean) =>
  isSelected ? normalizeImagePath(product.image) ?? DefaultIcon : normalizeImagePath(product.image2) ?? DefaultDarkIcon;

const selectedCardSx: SxProps = {
  border: '2px solid',
  borderColor: 'primary.main',
};

const primaryHoverColor = '#1592FF1F';

const getContainerSx = (layout: 'small' | 'large', isSelected: boolean): SxProps => {
  const base: SxProps = {
    bgcolor: isSelected ? 'primary.dark' : 'background.paper',
    overflow: 'hidden',
    transition: 'transform 0.3s ease-in-out',
    cursor: 'pointer',
    border: '1px solid',
    borderColor: 'divider',
    ...(isSelected && selectedCardSx),
  };

  if (layout === 'small') {
    return {
      ...base,
      display: 'flex',
      flexDirection: 'column',
      m: 1.8,
      width: '198px',
      height: '224px',
      ...(!isSelected && {
        '&:hover': { transform: 'translateY(-1px)', border: '1px solid', borderColor: 'primary.main' },
        '&:hover .product-image-box': { backgroundColor: primaryHoverColor },
      }),
    };
  }

  return {
    ...base,
    position: 'relative',
    m: 2,
    width: '205px',
    height: '348px',
    pb: 4,
    '&:hover': {
      border: '1px solid',
      borderColor: 'primary.main',
      ...(!isSelected && { '& .product-image-box': { backgroundColor: primaryHoverColor } }),
    },
  };
};

const ProductCard = ({ product, layout, isInCart = false, redirectToProductPage = false }: CardProps) => {
  const selectedProductId = useSolutionStore((s) => s.selectedProductId);
  const isSelected = selectedProductId === product.idProduct;
  const { data: user } = useUserInfo();
  const isLoggedIn = !!user;

  const handleClick = () =>
    useSolutionStore.setState((state) => ({
      selectedProductId: state.selectedProductId === product.idProduct ? null : product.idProduct,
    }));

  const handleClickRedirectToProductPage = () => {
    window.open(`/solutions/${product.idProduct}`, '_blank', 'noopener,noreferrer');
  };

  const activate = redirectToProductPage ? handleClickRedirectToProductPage : handleClick;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    activate();
  };

  const isSmall = layout === 'small';

  return (
    <Box
      role="button"
      tabIndex={0}
      data-testid="product-card"
      onClick={activate}
      onKeyDown={handleKeyDown}
      sx={getContainerSx(layout, isSelected)}
    >
      <Box className="product-image-box" sx={{ display: 'flex', justifyContent: 'flex-start', p: 4 }}>
        <Image
          src={cardImageSrc(product, isSelected)}
          alt={product.product}
          width={42}
          height={42}
          style={{ objectFit: 'contain', objectPosition: 'left' }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          color: isSelected ? 'primary.contrastText' : 'inherit',
          ...(isSmall && { p: 2, textAlign: 'start', mb: 2, mt: 'auto' }),
        }}
      >
        <ProductName productName={product.product} isSelected={isSelected} />
        {!isSmall && (
          <ProductDescription description={(product as ProductSimple).description} isSelected={isSelected} />
        )}
      </Box>

      {!isSmall && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            display: 'flex',
            justifyContent: isLoggedIn ? 'flex-end' : 'space-between',
            p: 4,
          }}
        >
          {!isLoggedIn && <AddToCartButton product={product} isInCart={isInCart} isSelected={isSelected} />}
          <Button
            variant="outlined"
            startIcon={<ArrowForwardOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            sx={
              isSelected
                ? {
                    borderColor: 'primary.contrastText',
                    backgroundColor: 'background.paper',
                    '&:hover': { backgroundColor: 'grey.50' },
                  }
                : undefined
            }
          >
            Open
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProductCard;
